/**
 * Utility functions for generating foreign key column names.
 * These must match the backend logic in src/models/pipeline_node.py make_foreign_key()
 */

/**
 * Makes a string SQL-safe by converting to lowercase and replacing non-alphanumeric chars with underscores.
 * Matches the backend's make_string_sql_safe function.
 */
export function makeStringSqlSafe(str: string): string {
  return str.toLowerCase().replace(/[^a-z0-9]/g, '_');
}

/**
 * Generates the foreign key column name for a relationship.
 * Matches the backend's make_foreign_key function in src/models/pipeline_node.py
 *
 * @param relationshipName - The name of the relationship (can be empty/null)
 * @param parentNodeName - The name of the parent node (fallback if no relationship name)
 * @returns The generated foreign key column name (e.g., "_plb_fk_customers")
 */
export function generateForeignKeyName(
  relationshipName: string | null | undefined,
  parentNodeName: string | null | undefined
): string {
  const baseName = relationshipName || parentNodeName || 'unknown';
  return '_plb_fk_' + makeStringSqlSafe(baseName);
}

/**
 * Gets the display name for a foreign key field.
 * For saved relationships, uses the relationship name or "Connection to <parent>".
 * For pending relationships, uses the pending relationship data.
 *
 * @param field - The field object
 * @param getRelationship - Function to get relationship by ID
 * @param getNodeName - Function to get node name by ID
 * @param pendingRelationships - Map of pending relationships keyed by field name/id
 * @returns The display name for the field
 */
export function getForeignKeyDisplayName(
  field: { id?: string; name?: string; label?: string; relationship_id?: string },
  getRelationship?: (relationshipId: string) => { name?: string; parent_node_id?: string } | undefined,
  getNodeName?: (nodeId: string) => string | undefined,
  pendingRelationships?: Map<string, { name?: string; parent_node_id?: string }>
): string | null {
  const fieldKey = field.id || field.name || '';

  // Check for existing relationship first
  if (field.relationship_id && getRelationship) {
    const relationship = getRelationship(field.relationship_id);
    if (relationship) {
      if (relationship.name) {
        return relationship.name;
      }
      if (relationship.parent_node_id && getNodeName) {
        const parentNodeName = getNodeName(relationship.parent_node_id);
        return `Connection to ${parentNodeName || 'Unknown Node'}`;
      }
    }
  }

  // Check for pending relationship
  if (pendingRelationships?.has(fieldKey)) {
    const pendingRel = pendingRelationships.get(fieldKey);
    if (pendingRel) {
      if (pendingRel.name) {
        return pendingRel.name;
      }
      if (pendingRel.parent_node_id && getNodeName) {
        const parentNodeName = getNodeName(pendingRel.parent_node_id);
        return `Connection to ${parentNodeName || 'Unknown Node'}`;
      }
    }
  }

  // No relationship data found
  return null;
}
